import { bodyWithoutRefs, flat, listOf, LISTED_ROLES, parseEntity } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { ulidTime } from '../entity/catalog';
import { CONTENT_BLOCK, contentBlock, editableText } from '../writing/textNote';
import { withList } from '../plan/edits';
import { withDefaultNote } from '../nook/nook';

/**
 * The page a thing opens on, as text: what its column reads out, apart from
 * what its switches and its margin hold.
 */

/**
 * An arc's column and its log. The title is the page's own head, and the log
 * is behind its switch — so the column is the prose between them.
 */
export function arcDocument(entity: VaultEntity): { prose: string; log: string } {
  const lines = bodyWithoutRefs(entity, LISTED_ROLES).split('\n');
  let at = 0;
  while (at < lines.length && (lines[at]!.trim() === '' || /^##\s+note\s*$/i.test(lines[at]!))) at++;
  if (/^#\s+/.test(lines[at] ?? '')) at++;
  const rest = lines.slice(at);
  const log = rest.findIndex((l) => /^##\s+log\s*$/i.test(l));
  const prose = (log < 0 ? rest : rest.slice(0, log)).join('\n').trim();
  return { prose, log: log < 0 ? '' : rest.slice(log + 1).join('\n').trim() };
}

/** The lines of an arc's `evidence:`, as written. */
export function evidenceOf(entity: VaultEntity): string[] {
  return listOf(entity.frontMatter['evidence']);
}

/**
 * A note as a page: its first line is its title, and the rest is what the
 * column reads. A note that holds more than one piece of content has no one
 * run of text, and is read, not edited.
 */
export function noteDocument(path: string, raw: string): { title: string; body: string; editable: boolean } {
  const editable = editableText(raw);
  const text = (editable ?? parseEntity(path, raw).body).replace(/^\s+/, '');
  const [first = '', ...rest] = text.split('\n');
  const title = first.replace(/^#{1,6}\s+/, '').trim() || path.replace(/^.*\//, '').replace(/\.md$/, '');
  return { title, body: rest.join('\n').trim(), editable: editable !== null };
}

/** When a note was written, from the id its file declares, and whether a
 *  session wrote it. */
export function writtenOf(entity: VaultEntity): { at: Date | null; bySession: boolean } {
  const ms = ulidTime(flat(entity.frontMatter['id']));
  return { at: ms ? new Date(ms) : null, bySession: flat(entity.frontMatter['origin']) === 'assistant' };
}

/** The paths an arc holds by `sources:`, anchors dropped, in its order. */
export function sourcePaths(entity: VaultEntity): { path: string; anchor: string | null }[] {
  return listOf(entity.frontMatter['sources']).map((entry) => {
    const [path = '', anchor] = entry.split(' ^');
    return { path: path.trim().replace(/^\[\[|\]\]$/g, ''), anchor: anchor?.trim() || null };
  });
}

/**
 * A new note on an arc: held through `sources:`, the way the app holds an
 * arc's members, and made the note Write opens — `write:` by its id, as a
 * nook names its own. The arc's prose is not touched.
 */
export function withArcNote(raw: string, notePath: string, noteId: string): string {
  const entity = parseEntity('', raw);
  const held = listOf(entity.frontMatter['sources']);
  const sources = held.some((s) => s.split(' ^')[0]!.trim() === notePath) ? held : [...held, notePath];
  return withDefaultNote(withList(raw, 'sources', sources), noteId);
}

/** One passage of an arc's prose: a message, or prose no device has put in
 *  markers yet. */
export interface ProseBlock {
  id: string | null;
  text: string;
}

const TITLE_LINE = /^#[ \t]+[^\n]*\n?/;

/**
 * Where an arc's prose lives: the passages between its front matter and its
 * `## Log` — its content blocks, apart from any a placed block wraps — and
 * whether the first opens on the title line, which is the arc's name and is
 * never part of the prose.
 */
function proseSpans(raw: string) {
  const front = raw.match(/^---\n[\s\S]*?\n---\n/);
  const start = front ? front[0].length : 0;
  const blocks = [...raw.matchAll(new RegExp(CONTENT_BLOCK.source, 'g'))].filter((b) => b.index >= start);
  const wrapped = [...raw.matchAll(/<!--pondering:block [^>]*-->[\s\S]*?<!--pondering:\/block-->/g)];
  const inside = (at: number, spans: RegExpExecArray[] | RegExpMatchArray[]) =>
    spans.some((s) => at >= s.index! && at < s.index! + s[0].length);

  let end = raw.length;
  const tail = raw.search(/<!--pondering:(?:unit |structure base64)/);
  if (tail >= start) end = tail;
  for (const log of raw.matchAll(/^## Log[ \t]*$/gm)) {
    if (log.index >= start && log.index < end && !inside(log.index, blocks)) {
      end = log.index;
      break;
    }
  }

  const passages = blocks.filter((b) => b.index + b[0].length <= end && !inside(b.index, wrapped));
  return { start, end, passages };
}

/**
 * A passage split into what the page shows and what it keeps: the title line
 * of the first passage and the whitespace around the text go back exactly as
 * they were, so reading a passage and writing it back unchanged changes no
 * byte.
 */
function parts(content: string, first: boolean): { prefix: string; text: string; suffix: string } {
  const title = first ? (content.match(TITLE_LINE)?.[0] ?? '') : '';
  const rest = content.slice(title.length);
  const lead = rest.match(/^\s*/)![0];
  const body = rest.slice(lead.length);
  const suffix = body.match(/\s*$/)![0];
  return { prefix: title + lead, text: body.slice(0, body.length - suffix.length), suffix };
}

/** The arc's prose as the passages it is stored as, each editable on its own. */
export function arcProse(raw: string): ProseBlock[] {
  const { start, end, passages } = proseSpans(raw);
  if (passages.length > 0) {
    return passages.map((b, i) => ({ id: b[1]!, text: parts(b[3]!, i === 0).text }));
  }
  return [{ id: null, text: plainParts(raw.slice(start, end)).text }];
}

/** A hand-made arc's region: its `## Note` head and title line, then prose. */
function plainParts(region: string): { prefix: string; text: string; suffix: string } {
  // A face renders above the body, as the importer reads it: stepped over.
  const face = region.match(/^\s*# Face[ \t]*\n(?:[^\n]+\n)*/)?.[0] ?? '';
  const after = region.slice(face.length);
  const head = face + (after.match(/^\s*(?:## Note[ \t]*\n)?\s*(?:#[ \t]+[^\n]*\n)?/)?.[0] ?? '');
  const { prefix, text, suffix } = parts(region.slice(head.length), false);
  return { prefix: head + prefix, text, suffix };
}

/** [text] set between [prefix] and [suffix], never run into either. */
function joined(prefix: string, text: string, suffix: string, more: boolean): string {
  if (!text) return prefix + suffix;
  const before = prefix && !prefix.endsWith('\n') ? `${prefix}\n\n` : prefix;
  const after = suffix || (more ? '\n\n' : '');
  return before + text + after;
}

/**
 * Passage [index] of an arc's prose rewritten as [next], inside its own
 * markers — so the app reads it back as an edit to that message — with the
 * title line, the other passages, the log and the machine tail left byte for
 * byte. Text that spells one of the vault's own markers is refused rather
 * than written, since no boundary could say where it ends.
 */
export function withArcProse(raw: string, index: number, next: string): string {
  if (next.includes('<!--pondering:')) return raw;
  const text = next.replace(/^\s+|\s+$/g, '');
  const { start, end, passages } = proseSpans(raw);

  if (passages.length === 0) {
    if (index !== 0) return raw;
    const { prefix, text: was, suffix } = plainParts(raw.slice(start, end));
    if (text === was) return raw;
    const rest = raw.slice(end);
    return raw.slice(0, start) + joined(prefix, text, suffix || (rest ? '' : '\n'), rest !== '') + rest;
  }

  const block = passages[index];
  if (!block) return raw;
  const { prefix, suffix } = parts(block[3]!, index === 0);
  const content = joined(prefix, text, suffix, false);
  if (content === block[3]) return raw;
  const at = block.index;
  return raw.slice(0, at) + contentBlock(block[1]!, content, block[2]) + raw.slice(at + block[0].length);
}
